import type { Rarity, ToyConfig } from '../types';

export interface ToyVisualConfig {
  color: string;
  faceColor: string;
  accentColor: string;
  emoji: string;
  shape: 'round' | 'square' | 'diamond' | 'blob';
  ears?: number;
  spots?: number;
}

export const TOY_VISUALS: Record<string, ToyVisualConfig> = {
  bear_brown:    { color: '#8B5A2B', faceColor: '#FFE4C4', accentColor: '#5C3A1E', emoji: '🐻', shape: 'round' },
  cat_little:    { color: '#F4A460', faceColor: '#FFF0F5', accentColor: '#D2691E', emoji: '🐱', shape: 'round' },
  rabbit_cloud:  { color: '#F0F8FF', faceColor: '#FFB6C1', accentColor: '#E6E6FA', emoji: '🐰', shape: 'round', ears: 2 },
  penguin_waddle:{ color: '#2F4F4F', faceColor: '#FFFFFF', accentColor: '#FFD700', emoji: '🐧', shape: 'diamond' },
  dog_cookie:    { color: '#D2B48C', faceColor: '#FFF8DC', accentColor: '#8B4513', emoji: '🐶', shape: 'round' },
  duck_bath:     { color: '#FFD700', faceColor: '#FF8C00', accentColor: '#FFA500', emoji: '🐥', shape: 'round' },
  frog_leaf:     { color: '#32CD32', faceColor: '#F0FFF0', accentColor: '#228B22', emoji: '🐸', shape: 'blob' },
  hamster_seed:  { color: '#F5DEB3', faceColor: '#FFF5EE', accentColor: '#DEB887', emoji: '🐹', shape: 'round' },
  fox_crystal:   { color: '#FF7F50', faceColor: '#FFF0F5', accentColor: '#FF4500', emoji: '🦊', shape: 'diamond' },
  penguin_ocean: { color: '#4169E1', faceColor: '#FFFFFF', accentColor: '#00BFFF', emoji: '🐧', shape: 'diamond' },
  owl_moon:      { color: '#483D8B', faceColor: '#FFE4B5', accentColor: '#FFD700', emoji: '🦉', shape: 'round', ears: 2 },
  deer_stardust: { color: '#DAA520', faceColor: '#FFF8DC', accentColor: '#B8860B', emoji: '🦌', shape: 'blob' },
  sheep_dream:   { color: '#F8F8FF', faceColor: '#E6E6FA', accentColor: '#D8BFD8', emoji: '🐑', shape: 'round' },
  dragon_cosmic: { color: '#4B0082', faceColor: '#E0FFFF', accentColor: '#9400D3', emoji: '🐉', shape: 'diamond' },
  phoenix_dream: { color: '#FF4500', faceColor: '#FFD700', accentColor: '#FF6347', emoji: '🔥', shape: 'diamond' },
};

export const TOYS: ToyConfig[] = [
  // Common x8
  { id: 'bear_brown', name: 'Forest Bear', rarity: 'common', spawnWeight: 0.08, image: 'toy_bear', thumbnail: 'thumb_bear' },
  { id: 'cat_little', name: 'Little Cat', rarity: 'common', spawnWeight: 0.08, image: 'toy_cat', thumbnail: 'thumb_cat' },
  { id: 'rabbit_cloud', name: 'Cloud Rabbit', rarity: 'common', spawnWeight: 0.08, image: 'toy_rabbit', thumbnail: 'thumb_rabbit' },
  { id: 'penguin_waddle', name: 'Waddle Penguin', rarity: 'common', spawnWeight: 0.08, image: 'toy_penguin', thumbnail: 'thumb_penguin' },
  { id: 'dog_cookie', name: 'Cookie Dog', rarity: 'common', spawnWeight: 0.08, image: 'toy_dog', thumbnail: 'thumb_dog' },
  { id: 'duck_bath', name: 'Bath Duck', rarity: 'common', spawnWeight: 0.08, image: 'toy_duck', thumbnail: 'thumb_duck' },
  { id: 'frog_leaf', name: 'Leaf Frog', rarity: 'common', spawnWeight: 0.07, image: 'toy_frog', thumbnail: 'thumb_frog' },
  { id: 'hamster_seed', name: 'Seed Hamster', rarity: 'common', spawnWeight: 0.07, image: 'toy_hamster', thumbnail: 'thumb_hamster' },
  // Rare x5
  { id: 'fox_crystal', name: 'Crystal Fox', rarity: 'rare', spawnWeight: 0.06, image: 'toy_fox', thumbnail: 'thumb_fox' },
  { id: 'penguin_ocean', name: 'Ocean Penguin', rarity: 'rare', spawnWeight: 0.06, image: 'toy_penguin2', thumbnail: 'thumb_penguin2' },
  { id: 'owl_moon', name: 'Moon Owl', rarity: 'rare', spawnWeight: 0.05, image: 'toy_owl', thumbnail: 'thumb_owl' },
  { id: 'deer_stardust', name: 'Stardust Deer', rarity: 'rare', spawnWeight: 0.05, image: 'toy_deer', thumbnail: 'thumb_deer' },
  { id: 'sheep_dream', name: 'Dream Sheep', rarity: 'rare', spawnWeight: 0.05, image: 'toy_sheep', thumbnail: 'thumb_sheep' },
  // Legendary x2
  { id: 'dragon_cosmic', name: 'Cosmic Dragon', rarity: 'legendary', spawnWeight: 0.02, image: 'toy_dragon', thumbnail: 'thumb_dragon' },
  { id: 'phoenix_dream', name: 'Dream Phoenix', rarity: 'legendary', spawnWeight: 0.02, image: 'toy_phoenix', thumbnail: 'thumb_phoenix' },
];

export function getVisualForToy(id: string): ToyVisualConfig {
  return TOY_VISUALS[id] ?? {
    color: '#a0a0a0',
    faceColor: '#ffffff',
    accentColor: '#666666',
    emoji: '❓',
    shape: 'round',
  };
}

export function getRarityColor(rarity: Rarity): string {
  const map: Record<Rarity, string> = {
    common: '#006b5f',
    rare: '#a43a3d',
    legendary: '#c3a300',
  };
  return map[rarity];
}

export function getRarityGlow(rarity: Rarity): number {
  const map: Record<Rarity, number> = {
    common: 0x00c2a6,
    rare: 0xff6b6b,
    legendary: 0xffd700,
  };
  return map[rarity];
}
